import {
  afterNextRender,
  Component,
  ElementRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  computed,
  effect,
  signal,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AccountApiService } from '../../core/api/account-api.service';
import { mapPackageSelectionToUi } from '../../core/api/product-master/mappers/map-package-selection-to-ui';
import type { PackageSelectionItem } from '../../core/api/product-master/models/package-selection.model';
import { ProductMasterApiService } from '../../core/api/product-master/product-master-api.service';
import { ServiceAvailabilityApiService } from '../../core/api/service-availability-api.service';
import { AnalyticsService } from '../../core/integrations/analytics';
import { TranslatePipe, TranslationService } from '../../core/i18n';
import {
  SHIPMENT_WIZARD_STEPS,
  ShipmentFlowStateService,
} from '../../core/services/shipment-flow-state.service';
import { environment } from '../../../environments/environment';
import { AlertToast } from '../../shared/components/alert-toast/alert-toast';
import { readPageSkeletonPreviewQueryFlag } from '../../shared/utils/e2e-query-flags';
import { DelayedLoadingController } from '../../shared/utils/delayed-loading';
import { redirectIfCompletedShipmentActive } from '../get-labels/utils/redirect-if-completed-shipment-active';
import { ShipmentStepper } from '../ship-from-ship-to/sections/shipment-stepper/shipment-stepper';
import {
  collectCustomerPackagingBannerErrors,
  customerDimensionExceedParams,
  type CustomerPackagingBannerErrors,
} from './customer-packaging-banner-errors';
import {
  applyCustomerPieceRowValidators,
  createExpressPackWeightValidators,
  createPackagePieceRowGroup,
  createPackageSelectionForm,
  createTrackingReferenceControl,
  CUSTOMER_DEFAULT_PER_PIECE_WEIGHT,
  expressPackDefaultWeightForUnit,
  expressPackMaxWeightForUnit,
  roundMeasurementUp,
  type PackageSelectionFormValue,
} from './forms/package-selection.form';
import type { PackageTypeId, PackageTypeOption, PackageUnitSystem } from './package-types.config';
import { isCustomerPackaging, isExpressBox, isExpressPackOrBox } from './package-types.config';
import { CustomerPackagingDetails } from './sections/customer-packaging-details/customer-packaging-details';
import { DeliveryInstructionsSection } from './sections/delivery-instructions-section/delivery-instructions-section';
import { ExpressPackagingDetails } from './sections/express-packaging-details/express-packaging-details';
import { HowToMeasureDialog } from './sections/how-to-measure-dialog/how-to-measure-dialog';
import { PackageSelectionActions } from './sections/package-selection-actions/package-selection-actions';
import { PackageSelectionSkeleton } from './sections/package-selection-skeleton/package-selection-skeleton';
import { PackageTypeSelector } from './sections/package-type-selector/package-type-selector';
import { TrackingReferencesSection } from './sections/tracking-references-section/tracking-references-section';
import {
  extractPsaAgentWeightProducts,
  resolveEffectivePackageWeightMaxLb,
  type PsaAgentWeightProduct,
} from './utils/resolve-effective-package-weight-max';

const EMPTY_BANNER_ERRORS: CustomerPackagingBannerErrors = {
  shipmentErrors: [],
  packageGroups: [],
  showSurchargeFooter: false,
  hasBlockingErrors: false,
};

/**
 * Create-shipment Package step (step 2) composed from section components.
 * Catalog and constraints come from Product Master package-selection.
 */
@Component({
  selector: 'app-package-selection',
  imports: [
    ReactiveFormsModule,
    AlertToast,
    TranslatePipe,
    ShipmentStepper,
    PackageTypeSelector,
    CustomerPackagingDetails,
    ExpressPackagingDetails,
    TrackingReferencesSection,
    DeliveryInstructionsSection,
    HowToMeasureDialog,
    PackageSelectionActions,
    PackageSelectionSkeleton,
  ],
  templateUrl: './package-selection.html',
  styleUrl: './package-selection.scss',
})
export class PackageSelection implements OnInit, OnDestroy {
  private readonly shipmentFlowState = inject(ShipmentFlowStateService);
  private readonly router = inject(Router);
  private readonly productMasterApi = inject(ProductMasterApiService);
  private readonly serviceAvailabilityApi = inject(ServiceAvailabilityApiService);
  private readonly accountApi = inject(AccountApiService);
  private readonly analytics = inject(AnalyticsService);
  private readonly translation = inject(TranslationService);
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);

  protected readonly activeStepIndex = 1;
  protected readonly form = createPackageSelectionForm();
  /** Package cards mapped from Product Master (empty until load completes). */
  protected readonly visiblePackageTypes = signal<readonly PackageTypeOption[]>([]);
  /** Raw catalog from Product Master; locale only remaps this — never refetches on toggle. */
  private readonly packageSelectionItems = signal<PackageSelectionItem[] | null>(null);
  /** PSA available products from Package-step service-availability (agent weight limits). */
  private readonly psaProducts = signal<readonly PsaAgentWeightProduct[]>([]);
  private packageSelectionRequestId = 0;
  protected readonly measureDialogOpen = signal(false);
  protected readonly showValidationErrors = signal(false);
  /** True when Product Master package-selection or PSA fetch fails. */
  protected readonly packageCatalogLoadFailed = signal(false);
  /**
   * True when the delayed catalog skeleton should paint (OM-711: after 1s pending, min 0.5s hold).
   */
  protected readonly showPackageSkeleton = signal(false);
  private readonly delayedCatalogLoading = new DelayedLoadingController((show) => {
    this.showPackageSkeleton.set(show);
  });
  /**
   * When true, keeps the catalog skeleton visible for E2E/QA preview (`?pageSkeletonPreview=1`).
   */
  private readonly pageSkeletonPreview = signal(readPageSkeletonPreviewQueryFlag());
  /**
   * True while Product Master catalog has not settled (success or failure).
   * Keeps the content area blank during the pre-skeleton window (<1s) before a skeleton may appear.
   */
  protected readonly catalogPending = computed(
    () =>
      this.pageSkeletonPreview() ||
      (this.packageSelectionItems() === null && !this.packageCatalogLoadFailed()),
  );
  /** True while account lookup is in flight on Save and Continue. */
  protected readonly accountLookupInProgress = signal(false);
  /** Generic account lookup failure banner key (i18n). */
  protected readonly accountLookupError = signal<string | null>(null);
  /** Blocking Customer Packaging red-banner model (shipment limits, field max, girth). */
  protected readonly customerBannerErrors =
    signal<CustomerPackagingBannerErrors>(EMPTY_BANNER_ERRORS);
  protected readonly selectedPackageTypeId = signal<PackageTypeId | null>(
    this.form.controls.packageTypeId.value,
  );
  protected readonly unitSystem = signal<PackageUnitSystem>(this.form.controls.unitSystem.value);

  protected readonly selectedPackageType = computed(() => {
    const selectedId = this.selectedPackageTypeId();
    return this.visiblePackageTypes().find((type) => type.id === selectedId) ?? null;
  });

  protected readonly selectedRequiresDimensions = computed(
    () => this.selectedPackageType()?.requiresDimensions ?? false,
  );

  protected readonly selectedConstraints = computed(
    () => this.selectedPackageType()?.constraints ?? null,
  );

  /**
   * Catalog weight max clamped by PSA agent limits for the selected package type (OM-872).
   */
  protected readonly effectiveMaxWeightLb = computed(() => {
    const catalogMax = this.selectedConstraints()?.weight.max;
    const typeId = this.selectedPackageTypeId();
    const item = this.packageSelectionItems()?.find((row) => row.packageTypeCode === typeId);
    return resolveEffectivePackageWeightMaxLb(
      catalogMax,
      item?.productCodes ?? [],
      this.psaProducts(),
    );
  });

  /**
   * Selected package constraints with `weight.max` replaced by the effective (PSA-clamped) max.
   * Keeps Express / Customer packaging helper copy aligned with validators.
   */
  protected readonly effectiveSelectedConstraints = computed(() => {
    const constraints = this.selectedConstraints();
    if (!constraints) {
      return null;
    }
    const maxWeightLb = this.effectiveMaxWeightLb();
    if (typeof maxWeightLb !== 'number' || maxWeightLb === constraints.weight.max) {
      return constraints;
    }
    return {
      ...constraints,
      weight: {
        ...constraints.weight,
        max: maxWeightLb,
      },
    };
  });

  protected readonly packagePieceCount = signal(this.form.controls.pieces.length);
  /**
   * Per-row remount keys so Clear recreates puro-input hosts after programmatic reset to defaults.
   */
  protected readonly pieceRowViewKeys = signal<number[]>([0]);

  /** Last type used for customer piece-validator membership (avoids no-op re-apply). */
  private pieceValidatorsCustomerMembership: boolean | null = null;

  constructor() {
    this.restorePersistedState();

    effect(() => {
      const locale = this.translation.locale() === 'fr' ? 'fr' : 'en';
      const cached = this.packageSelectionItems();

      if (cached !== null) {
        this.visiblePackageTypes.set(mapPackageSelectionToUi(cached, locale));
        this.resyncValidatorsAfterCatalogReady();
        return;
      }

      this.schedulePackageSelectionLoad(locale);
    });

    effect(() => {
      const failed = this.packageCatalogLoadFailed();
      const items = this.packageSelectionItems();
      const catalogPending = this.pageSkeletonPreview() || (items === null && !failed);

      if (catalogPending) {
        this.delayedCatalogLoading.setPending(true);
        return;
      }

      if (failed) {
        this.delayedCatalogLoading.fail();
        return;
      }

      if (items !== null) {
        this.delayedCatalogLoading.setPending(false);
      }
    });
  }

  /**
   * Clears delayed skeleton timers when leaving the Package step.
   */
  ngOnDestroy(): void {
    this.delayedCatalogLoading.destroy();
  }

  /**
   * Redirects to Ship From / Ship To when addresses are incomplete or SA attestation is missing.
   * Catalog fetch requires JWT_sa; JWT_avs is kept in flow state for UI/audit but is not sent to Product Master.
   * Keeps `ngOnInit` void-returning per `OnInit`; navigation errors are ignored.
   */
  ngOnInit(): void {
    if (redirectIfCompletedShipmentActive(this.shipmentFlowState, this.router)) {
      return;
    }

    const state = this.shipmentFlowState.load();

    if (
      !state?.completedSteps.includes(SHIPMENT_WIZARD_STEPS.addresses) ||
      !state.attestations?.serviceAbility?.trim()
    ) {
      this.router.navigateByUrl('/').catch(() => undefined);
    }
  }

  /**
   * Restores Package step values from session storage when navigating back from a later step.
   * Rebuilds FormArrays (pieces / tracking refs) because patchValue alone cannot grow them.
   */
  private restorePersistedState(): void {
    const persisted = this.shipmentFlowState.load()?.package;
    if (!persisted) {
      return;
    }

    this.applyPersistedPackageValue(persisted);
  }

  /**
   * Applies a saved package form snapshot to the live form and mirror signals.
   */
  private applyPersistedPackageValue(persisted: PackageSelectionFormValue): void {
    const pieces = this.form.controls.pieces;
    pieces.clear();
    const pieceRows =
      persisted.pieces.length > 0
        ? persisted.pieces
        : [
            {
              identicalPieces: 1,
              perPieceWeight: CUSTOMER_DEFAULT_PER_PIECE_WEIGHT,
              length: null,
              width: null,
              height: null,
              requiresAdditionalHandling: false,
            },
          ];

    for (const row of pieceRows) {
      pieces.push(
        createPackagePieceRowGroup(row, {
          unitSystem: persisted.unitSystem,
        }),
      );
    }

    const trackingReferences = this.form.controls.trackingReferences;
    trackingReferences.clear();
    trackingReferences.push(createTrackingReferenceControl(persisted.trackingReferences[0] ?? ''));

    this.form.patchValue({
      packageTypeId: persisted.packageTypeId,
      unitSystem: persisted.unitSystem,
      deliveryInstructions: persisted.deliveryInstructions ?? '',
      expressTotalWeight: persisted.expressTotalWeight,
      expressRequiresAdditionalHandling: persisted.expressRequiresAdditionalHandling,
    });

    this.selectedPackageTypeId.set(persisted.packageTypeId);
    this.unitSystem.set(persisted.unitSystem);
    this.packagePieceCount.set(pieces.length);
    this.pieceRowViewKeys.set(pieceRows.map(() => 0));

    if (persisted.packageTypeId) {
      this.syncValidatorsForPackageType(persisted.packageTypeId);
    }
  }

  /**
   * Re-applies type-aware validators once Product Master constraints are available after restore.
   */
  private resyncValidatorsAfterCatalogReady(): void {
    const typeId = this.selectedPackageTypeId();
    if (typeId) {
      this.syncValidatorsForPackageType(typeId);
    }
  }

  /**
   * Starts a catalog load without returning a Promise to the Angular `effect` (avoids constructor async smells).
   */
  private schedulePackageSelectionLoad(locale: 'en' | 'fr'): void {
    this.loadPackageSelection(locale).catch(() => {
      /* Catalog failure is handled inside loadPackageSelection via packageCatalogLoadFailed. */
    });
  }

  /**
   * Fetches the package catalog and Product Service Availability in parallel using JWT_sa.
   * Both must succeed and PSA must return JWT_psa; otherwise the existing catalog-load error path runs.
   * Locale changes remap cached items only; ignores stale responses via request id.
   */
  private async loadPackageSelection(locale: 'en' | 'fr'): Promise<void> {
    const state = this.shipmentFlowState.load();
    const serviceAbilityToken = state?.attestations?.serviceAbility?.trim();
    if (!serviceAbilityToken) {
      return;
    }

    const requestId = ++this.packageSelectionRequestId;

    try {
      const [items, psaResponse] = await Promise.all([
        this.productMasterApi.getPackageSelection({
          serviceAbilityToken,
        }),
        this.serviceAvailabilityApi.check({
          serviceAbilityToken,
        }),
      ]);
      if (requestId !== this.packageSelectionRequestId) {
        return;
      }

      const psaAttestation = psaResponse.attestation?.trim();
      if (!psaAttestation) {
        throw new Error('Missing service-availability attestation');
      }

      this.shipmentFlowState.saveAttestation('serviceAvailability', psaAttestation);
      this.psaProducts.set(extractPsaAgentWeightProducts(psaResponse));
      this.packageCatalogLoadFailed.set(false);
      this.packageSelectionItems.set(items);
      this.visiblePackageTypes.set(mapPackageSelectionToUi(items, locale));
      this.resyncValidatorsAfterCatalogReady();
    } catch {
      if (requestId !== this.packageSelectionRequestId) {
        return;
      }
      this.shipmentFlowState.clearAttestation('serviceAvailability');
      this.psaProducts.set([]);
      this.packageCatalogLoadFailed.set(true);
      this.packageSelectionItems.set([]);
      this.visiblePackageTypes.set([]);
    }
  }

  /**
   * Updates the selected package type from the radio list.
   * Pack/Box default express weight to 1; other types clear express weight so it is not saved stale.
   * Leaving Customer Packaging resets piece rows so SH dimensions are not persisted for Express types.
   * Syncs piece-row and Express weight validators so hidden fields do not block Save & Continue.
   */
  protected onPackageTypeChange(typeId: PackageTypeId): void {
    this.form.controls.packageTypeId.setValue(typeId);
    this.selectedPackageTypeId.set(typeId);
    this.showValidationErrors.set(false);
    this.customerBannerErrors.set(EMPTY_BANNER_ERRORS);

    if (!isCustomerPackaging(typeId)) {
      this.resetPiecesToDefaultRow();
      // Replacement rows start with customer validators; force a full re-sync below.
      this.pieceValidatorsCustomerMembership = null;
    }

    this.syncValidatorsForPackageType(typeId);

    if (isExpressPackOrBox(typeId)) {
      // Converted to the active unit system (OM-714): a raw `1` sent unconverted while metric
      // is active represents ~1 kg (~2.2 lb) and can exceed product max-weight constraints.
      this.form.controls.expressTotalWeight.setValue(
        expressPackDefaultWeightForUnit(this.unitSystem()),
      );
      if (!isExpressBox(typeId)) {
        this.form.controls.expressRequiresAdditionalHandling.setValue(false);
      }
    } else {
      this.form.controls.expressTotalWeight.setValue(null);
      this.form.controls.expressRequiresAdditionalHandling.setValue(false);
    }

    this.analytics.trackEvent({
      event: isCustomerPackaging(typeId) ? 'custom package' : 'standard package',
      myPackages: typeId,
    });
  }

  /**
   * Replaces Customer Packaging piece rows with a single empty default row.
   * Clears stale dimensions and AH flags after switching away from Customer Packaging.
   */
  private resetPiecesToDefaultRow(): void {
    const pieces = this.form.controls.pieces;
    pieces.clear();
    pieces.push(createPackagePieceRowGroup());
    this.packagePieceCount.set(1);
    this.pieceRowViewKeys.set([0]);
  }

  /**
   * Toggles piece-row validators when customer membership changes, and re-applies
   * unit-aware maxes while Customer Packaging is selected.
   * Always syncs Express Pack/Box weight validators for the selected type.
   */
  private syncValidatorsForPackageType(typeId: PackageTypeId): void {
    const isCustomer = isCustomerPackaging(typeId);

    if (this.pieceValidatorsCustomerMembership !== isCustomer || isCustomer) {
      this.syncCustomerPieceValidators(isCustomer);
      this.pieceValidatorsCustomerMembership = isCustomer;
    }

    this.syncExpressWeightValidators(typeId);
  }

  /**
   * Applies or clears Customer Packaging piece-row validators using PM constraints and unit.
   */
  private syncCustomerPieceValidators(enabled: boolean): void {
    const options = {
      unitSystem: this.unitSystem(),
      maxWeightLb: this.effectiveMaxWeightLb(),
      maxLengthIn: this.selectedConstraints()?.length.max,
    };

    for (const row of this.form.controls.pieces.controls) {
      applyCustomerPieceRowValidators(row, enabled, options);
    }
  }

  /**
   * Wires Express Pack/Box total-weight validators from effective (PSA-clamped) max and current unit.
   * Clears validators when leaving Pack/Box so other package types are not blocked.
   * Max weight is applied only when an effective `weight.max` is available.
   */
  private syncExpressWeightValidators(
    typeId: PackageTypeId | null = this.selectedPackageTypeId(),
  ): void {
    const weightControl = this.form.controls.expressTotalWeight;

    if (isExpressPackOrBox(typeId)) {
      weightControl.setValidators(
        createExpressPackWeightValidators(this.unitSystem(), this.effectiveMaxWeightLb()),
      );
    } else {
      weightControl.clearValidators();
    }

    weightControl.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Updates the measurement unit system for customer packaging or Express Pack/Box weight.
   * Recomputes max-weight validators without converting the typed value.
   */
  protected onUnitSystemChange(unitSystem: PackageUnitSystem): void {
    this.form.controls.unitSystem.setValue(unitSystem);
    this.unitSystem.set(unitSystem);

    const selectedId = this.selectedPackageTypeId();
    if (isExpressPackOrBox(selectedId)) {
      this.syncExpressWeightValidators(selectedId);
    } else if (isCustomerPackaging(selectedId)) {
      this.syncCustomerPieceValidators(true);
    }

    this.analytics.trackEvent({
      event: 'units',
      weightMetricUnit: unitSystem === 'metric' ? 'kg' : 'lb',
    });
  }

  /**
   * Appends another package piece row when existing rows are error-free and piece-count limits pass.
   * Per-piece weight/dimension maxes gate Add via row validators and the red banner; Total Weight does not.
   * Moves focus to the new package title after the view updates (audit ID 10).
   */
  protected onAddPackage(): void {
    this.markCustomerPieceRowsTouched();
    this.showValidationErrors.set(true);

    const banner = this.collectCustomerBannerErrors({ includeDefaultRowPieces: true });
    this.customerBannerErrors.set(banner);

    if (!this.areCustomerPieceRowsValid() || banner.hasBlockingErrors) {
      return;
    }

    this.customerBannerErrors.set(EMPTY_BANNER_ERRORS);
    const row = createPackagePieceRowGroup(undefined, {
      unitSystem: this.unitSystem(),
      maxWeightLb: this.effectiveMaxWeightLb(),
      maxLengthIn: this.selectedConstraints()?.length.max,
    });
    this.form.controls.pieces.push(row);
    this.packagePieceCount.set(this.form.controls.pieces.length);
    this.pieceRowViewKeys.update((keys) => [...keys, 0]);
    this.scheduleNewPackageTitleFocus(this.form.controls.pieces.length - 1);
  }

  /**
   * Navigates back to Ship From / Ship To when the footer Back action is used.
   */
  protected onBackRequested(): void {
    if (this.accountLookupInProgress()) {
      return;
    }
    void this.router.navigateByUrl('/');
  }

  /**
   * Moves keyboard focus to the newly added package title after the row renders.
   */
  private scheduleNewPackageTitleFocus(index: number): void {
    afterNextRender(
      () => {
        const title = this.hostElement.nativeElement.querySelector(
          `[data-testid="package-piece-title-${index}"]`,
        ) as HTMLElement | null;
        title?.focus();
      },
      { injector: this.injector },
    );
  }

  /**
   * Clears values for a piece row while keeping the row present.
   * Bumps the row view key so puro-input remounts, then re-applies defaults so the
   * kit CVA display shows 1 / 1 (not a stale blank until focus).
   */
  protected onClearRow(index: number): void {
    const row = this.form.controls.pieces.at(index);
    row.reset({
      identicalPieces: 1,
      perPieceWeight: CUSTOMER_DEFAULT_PER_PIECE_WEIGHT,
      length: null,
      width: null,
      height: null,
      requiresAdditionalHandling: false,
    });
    this.pieceRowViewKeys.update((keys) => {
      const next = keys.slice();
      while (next.length <= index) {
        next.push(0);
      }
      next[index] = (next[index] ?? 0) + 1;
      return next;
    });
    queueMicrotask(() => {
      row.controls.identicalPieces.setValue(1);
      row.controls.perPieceWeight.setValue(CUSTOMER_DEFAULT_PER_PIECE_WEIGHT);
    });
  }

  /**
   * Returns whether every Customer Packaging piece row passes field validators.
   */
  private areCustomerPieceRowsValid(): boolean {
    return this.form.controls.pieces.controls.every((row) => row.valid);
  }

  /**
   * Marks all Customer Packaging piece controls touched so inline errors surface on Add/Save.
   */
  private markCustomerPieceRowsTouched(): void {
    for (const row of this.form.controls.pieces.controls) {
      row.markAllAsTouched();
    }
  }

  /**
   * Removes a piece row when more than one exists.
   * Bumps remaining view keys so puro-input remounts after FormArray indices shift.
   */
  protected onRemoveRow(index: number): void {
    if (this.form.controls.pieces.length <= 1) {
      return;
    }

    this.form.controls.pieces.removeAt(index);
    this.packagePieceCount.set(this.form.controls.pieces.length);
    this.pieceRowViewKeys.update((keys) =>
      keys.filter((_, i) => i !== index).map((key) => key + 1),
    );
  }

  /**
   * Opens the how-to-measure dialog.
   */
  protected openMeasureDialog(): void {
    this.measureDialogOpen.set(true);
  }

  /**
   * Closes the how-to-measure dialog.
   */
  protected closeMeasureDialog(): void {
    this.measureDialogOpen.set(false);
  }

  /**
   * Shows client-side validation for the Package step. On success, stores the
   * selected item's package-selection JWT, looks up the demo billing account,
   * stores JWT_acct, persists package state, and advances to the Service step.
   * Stays on this step when validation, package-selection JWT, or account lookup fails.
   * Normalizes per-piece weight up to whole numbers; dimensions keep entered decimals.
   */
  protected async onSaveAttempted(): Promise<void> {
    if (this.accountLookupInProgress()) {
      return;
    }

    for (const row of this.form.controls.pieces.controls) {
      row.patchValue({
        perPieceWeight: this.normalizeMeasurement(row.controls.perPieceWeight.value),
      });
    }

    for (const control of this.form.controls.trackingReferences.controls) {
      control.updateValueAndValidity({ emitEvent: false });
    }

    this.form.markAllAsTouched();
    this.showValidationErrors.set(true);
    this.accountLookupError.set(null);

    const isCustomer = isCustomerPackaging(this.selectedPackageTypeId());
    const banner = isCustomer ? this.collectCustomerBannerErrors() : EMPTY_BANNER_ERRORS;
    this.customerBannerErrors.set(banner);

    if (!this.form.valid || banner.hasBlockingErrors) {
      return;
    }

    if (!this.persistSelectedPackageAttestation()) {
      this.packageCatalogLoadFailed.set(true);
      return;
    }

    const accountNumber = environment.standardAccountNumber?.trim() ?? '';
    if (!accountNumber) {
      this.accountLookupError.set('createShipment.packageStep.details.accountLookupError');
      return;
    }

    this.accountLookupInProgress.set(true);
    try {
      const response = await this.accountApi.lookup({ accountNumber });
      if (
        !response.canContinue ||
        typeof response.attestation !== 'string' ||
        !response.attestation
      ) {
        this.accountLookupError.set('createShipment.packageStep.details.accountLookupError');
        return;
      }

      this.shipmentFlowState.saveAttestation('account', response.attestation);
      this.shipmentFlowState.savePackageSelection(this.form.getRawValue());
      await this.router.navigate(['/service']);
    } catch {
      this.accountLookupError.set('createShipment.packageStep.details.accountLookupError');
    } finally {
      this.accountLookupInProgress.set(false);
    }
  }

  /**
   * Aligns session state with the selected catalog item's package-selection JWT.
   * Saves when present; otherwise clears any prior packageSelection token so a
   * stale JWT cannot travel with a different package type.
   *
   * @returns true when a non-empty attestation was stored; false when missing
   */
  private persistSelectedPackageAttestation(): boolean {
    const selectedId = this.selectedPackageTypeId();
    const selectedItem = this.packageSelectionItems()?.find(
      (item) => item.packageTypeCode === selectedId,
    );
    const attestation = selectedItem?.attestation?.trim();
    if (attestation) {
      this.shipmentFlowState.saveAttestation('packageSelection', attestation);
      return true;
    }
    this.shipmentFlowState.clearAttestation('packageSelection');
    return false;
  }

  /**
   * Builds the Customer Packaging red-banner model for Add/Save gates.
   *
   * @param options.includeDefaultRowPieces - When true (Add), treat an extra default row of 1 piece
   */
  private collectCustomerBannerErrors(
    options: { includeDefaultRowPieces?: boolean } = {},
  ): CustomerPackagingBannerErrors {
    if (!isCustomerPackaging(this.selectedPackageTypeId())) {
      return EMPTY_BANNER_ERRORS;
    }

    const constraints = this.selectedConstraints();
    return collectCustomerPackagingBannerErrors(this.form.controls.pieces, {
      unitSystem: this.unitSystem(),
      maxWeightLb: this.effectiveMaxWeightLb(),
      maxLengthIn: constraints?.length.max,
      maxGirthIn: constraints?.girth.max,
      includeDefaultRowPieces: options.includeDefaultRowPieces,
    });
  }

  /**
   * Resolves dual-unit max weight params for the Customer Packaging total-weight summary message.
   * Empty when Product Master omits `constraints.weight.max` (after PSA agent clamp).
   */
  protected customerMaxWeightParams(): Record<string, string> {
    const maxWeightLb = this.effectiveMaxWeightLb();
    if (typeof maxWeightLb !== 'number' || !Number.isFinite(maxWeightLb)) {
      return {};
    }

    return {
      maxLb: String(expressPackMaxWeightForUnit('imperial', maxWeightLb)),
      maxKg: String(expressPackMaxWeightForUnit('metric', maxWeightLb)),
    };
  }

  /**
   * Destination residential for SH10 (service-ability flag or ship-to form).
   */
  protected isDestinationResidential(): boolean {
    const state = this.shipmentFlowState.load();
    return state?.destinationResidential === true || state?.addresses?.shipTo?.residential === true;
  }

  /**
   * Resolves dual-unit max length params for per-piece dimension exceed banner copy.
   * Empty when Product Master omits `constraints.length.max`.
   */
  protected customerMaxLengthParams(): Record<string, string> {
    const maxLengthIn = this.selectedConstraints()?.length.max;
    if (typeof maxLengthIn !== 'number' || !Number.isFinite(maxLengthIn)) {
      return {};
    }

    return customerDimensionExceedParams(maxLengthIn);
  }

  /**
   * Normalizes a numeric measurement by rounding decimals up to the next whole number.
   */
  protected normalizeMeasurement(value: number | null): number | null {
    if (value === null || !Number.isFinite(value)) {
      return value;
    }

    return roundMeasurementUp(value);
  }
}
